import { useEffect, useMemo, useState } from 'react';

import useThemeStore from '../../utils/hooks/useThemeStore';

/**
 * Chart colours that follow the app's theme.
 *
 * The store's value can be 'system' (and null before it initialises), so the
 * effective mode is resolved against a live media query rather than assumed.
 */
export default function useChartTheme() {
  const theme = useThemeStore((state) => state.theme);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query?.addEventListener) return undefined;
    const listener = (event) => setSystemDark(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  const isDark = theme === 'dark' || (theme !== 'light' && systemDark);

  return useMemo(
    () => ({
      isDark,
      tick: isDark ? '#94a3b8' : '#64748b',
      grid: isDark ? 'rgba(148,163,184,0.16)' : 'rgba(100,116,139,0.14)',
      text: isDark ? '#e2e8f0' : '#0f172a',
      surface: isDark ? '#1e293b' : '#ffffff',
      tooltip: {
        backgroundColor: isDark ? '#0f172a' : '#ffffff',
        titleColor: isDark ? '#e2e8f0' : '#0f172a',
        bodyColor: isDark ? '#cbd5e1' : '#334155',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderWidth: 1,
      },
    }),
    [isDark],
  );
}
